import React, { useEffect, useState } from 'react'
import useTrans from '@hooks/useTrans'
import { cn, getUserType } from '@utils/safeTs'
import { OrdineDetailType, RootStateType, UserType } from '@utils/types'
import { ArrowLeft, ChevronDown, LogoSipec } from '@components/icons'
import styles from './FlussoPersonalizzazioneBase.module.sass'
import { Button, PosizionePersonalizzazioneOrdineStatus, StatoOrdine } from '@components/atoms'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import { useRouter } from 'next/router'

interface Props {
  title?: string
  details?: { label: string, value: string }[]
  ordine: OrdineDetailType
  statusPosizioni?: { [k: number]: '' | 'completed' | 'unsaved' }
  personalizzazioniOpen: number[]
  posizioniOpen: number[]
  setPersonalizzazioniOpen: (v: number[]) => void
  setPosizioniOpen: (v: number[]) => void
  userType: UserType
  className?: string
  children?: React.ReactNode
}

const FlussoPersonalizzazioneBase = (props: Props) => {
  const { 
    title,
    details = [],
    ordine,
    statusPosizioni,
    posizioniOpen,
    setPosizioniOpen,
    personalizzazioniOpen,
    setPersonalizzazioniOpen,
    userType,
    className = '',
    children = undefined,
  } = props

  const t = useTrans()
  const router = useRouter()
  const fromAdmin = router.query.from_admin === 'true'
  const getOrdini = (ordine: OrdineDetailType) => ordine?.isParent ? ordine.ordini || [] : [ordine]
  const [openPersonalizzazioni, setOpenPersonalizzazioni] = useState(getOrdini(ordine).reduce((res, o) => res.concat(o.personalizzazioniOrdine?.map(p => p.id) || []), []))
  const [backUrl, setBackUrl] = useState(undefined)

  const [activePos, setActivePos] = useState(null)
  const onPosizioneClick = (posId) => {
    const personalizzazioneId = getOrdini(ordine)?.map((o) => o.personalizzazioniOrdine?.filter(p => p.posizioniPersonalizzazioneOrdine.filter(pos => pos.id === posId).length)?.[0]?.id).filter(Boolean)?.[0]
    // apro la personalizzazione
    if (personalizzazioneId && !personalizzazioniOpen.includes(personalizzazioneId))
      setPersonalizzazioniOpen(personalizzazioniOpen.concat([personalizzazioneId]))
    // apro la posizione
    if (!posizioniOpen.includes(posId))
      setPosizioniOpen(posizioniOpen.concat([posId]))
    setTimeout(() => {
      // Metto un timeout per dare tempo alle posizioni di aprirsi
      const el = document.getElementById(`posizione_${posId}`)
      if (el){
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  useEffect(() => {

    const fromDocumenti = document?.referrer 
      ? document.referrer.includes('/myaccount/documenti/ordini/')
      : null
    setBackUrl(document?.referrer
      ? userType === 'cliente' || fromDocumenti
        ? `/myaccount/documenti/ordini/${ordine.numeroProtocollo}` 
        : `/myaccount/gestione/ordini/${ordine.numeroProtocollo}` 
      : undefined
    )

    let rafId = 0
    let ticking = false

    const updateActivePos = () => {
      const elements = document.querySelectorAll<HTMLDivElement>("div[id^='posizione_']")
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight
      let activeEl: HTMLDivElement | null = null

      for (let i = 0; i < elements.length; i += 1) {
        const el = elements[i]
        const rect = el.getBoundingClientRect()
        if (rect.top < viewportHeight && rect.bottom > 0) {
          activeEl = el
          break
        }
      }

      const nextId = activeEl ? Number(activeEl.id.replace('posizione_', '')) : null
      setActivePos((prev) => (prev === nextId ? prev : nextId))
      ticking = false
    }

    const onScroll = () => {
      if (ticking) return
      ticking = true
      rafId = window.requestAnimationFrame(updateActivePos)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    onScroll()

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (rafId) window.cancelAnimationFrame(rafId)
    }
  }, [])

  return (
    <div className={cn(styles.root, backUrl && !fromAdmin ? styles['root--back'] : '', className)}>
      <section className={styles.body}>
        <div className="w-container-fluid">
          <div className={styles.layout}>
            <aside className={styles.sidebar}>
              <div className={styles.menu}>
                {getOrdini(ordine).map(o => (
                  <React.Fragment key={o.id}>
                    {o.personalizzazioniOrdine?.map(p => (
                      <div key={p.id} className={cn(styles.accordion, openPersonalizzazioni.includes(p.id) ? styles['accordion--open'] : '', p.posizioniPersonalizzazioneOrdine.map(pos => pos.id).includes(activePos) ? styles['accordion--active'] : '')}>
                        <button
                          type="button"
                          className={styles.accordionTrigger}
                          id={`accordion_${p.id}`}
                          aria-expanded={openPersonalizzazioni.includes(p.id)}
                          aria-controls={`accordion_panel_${p.id}`}
                          onClick={() => setOpenPersonalizzazioni((prev) => prev.includes(p.id) ? prev.filter(x => x !== p.id) : prev.concat([p.id]))}
                        >
                          <StatoOrdine level='posizione' userType={userType} showLabel={false} styleVariant='chip' stato={p.statoPersonalizzazione} />
                          <span className={styles.accordionTriggerLabel}>{p.nomeLavorazione}</span>
                          <ChevronDown className={styles.accordionTriggerArrow} />
                        </button>
                        <div
                          id={`accordion_panel_${p.id}`}
                          className={styles.accordionContent}
                          role="region"
                          aria-labelledby={`accordion_${p.id}`}
                          hidden={!openPersonalizzazioni.includes(p.id)}
                        >
                          {p.posizioniPersonalizzazioneOrdine.map((posizione) => (
                            <button key={posizione.id} type="button" className={cn(styles.menuItem, activePos === posizione.id ? styles['menuItem--active'] : '')} onClick={() => onPosizioneClick?.(posizione.id)}>
                              <StatoOrdine level='posizione' userType={userType} showLabel={false} stato={posizione.statoPosizionePersonalizzazione} />
                              <span className={styles.menuItemLabel}>{posizione.posizione.nome}</span>
                              <PosizionePersonalizzazioneOrdineStatus status={statusPosizioni?.[posizione.id] || ''} variant='icon' className={styles.menuItemStatusIcon} />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </aside>
            <div className={styles.content}>
              <div className={styles.details}>
                {backUrl && !fromAdmin && (
                  <div className={styles.backBox}>
                    <Button
                      label={t("Torna all'ordine")}
                      href={backUrl}
                      variant='ghost'
                      size="medium"
                      icon={<ArrowLeft />}
                      icon_pos='left' 
                    />
                  </div>
                )}
                <h1 className={styles.title}>{title}</h1>
                <div className={styles.detailsList}>

                  <p className={styles.detailsRow}>
                    <span>{t('Numero documento:')}{' '}</span>
                    <strong>{_.uniq(getOrdini(ordine).map(o => `${o.numeroDocumento}${o.ordineFornitore ? ` (${o.ordineFornitore.numeroDocumento})` : ''}`).filter(Boolean)).join(" - ")}</strong>
                  </p>
                  <p className={styles.detailsRow}>
                    <span>{t('Riferimento:')}{' '}</span>
                    <strong>{_.uniq(getOrdini(ordine).map(o => o.riferimentoLavorazione).filter(Boolean)).join(" - ")}</strong>
                  </p>
                  {details?.map(x => (
                    <p className={styles.detailsRow} key={x.label}>
                      <span>{x.label}{' '}</span>
                      <strong>{x.value}</strong>
                    </p>
                  ))}
                </div>
              </div>

              <div className={styles.list}>
                {children}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default FlussoPersonalizzazioneBase
